import { Subject, Subscription, Unsubscribable } from "rxjs";

/**
 * So what is 'full' and what is 'partial' update? Imagine the following scenario:
 * you enter a new chip into smart.search. but you don't click 'search' button.
 * then you go to time picker and change the time. A change of time will trigger
 * update to listeners immediately. What happens? The bucket of values will have
 * 'empty' value from smart.search and 'time' from time picker.
 * Is that correct? On one hand - yes, as you didn't click 'search' button that was supposed to
 * emit your input from smart.search.
 * On the other hand, that's nonsense, since you would like the search to use your
 * input from smart.search.
 * The cause of the problem - inconsistent UI design. Majority of widgets will emit an
 * update event as soon as user does something. Only smart.search was/is designed to delay
 * emit of values ONLY when 'search' button is clicked.
 * How to cope with that problem?
 * - I changed smart.search to emit chips as soon as you enter any valid expression.
 * - in addition to that, smart.search adds a flag, whether this output is 'full' or 'partial'.
 * - it will emit the flag 'full' ONLY when user clicked 'search' button (or user triggered drill down
 *   logic). In all other cases it will say 'partial'.
 * - the value.exchange.provider function isFullUpdate is looking for this flag (full/partial) and
 *   acts correspondingly/by suppressing update event/not passing it over to listeners if partial..
 */
export interface ITimestampedUpdate {
  rawValue:any;
  vexTimestamp:any
}

export type NotifyListeners = "stateless"|"stateful"|"all";


export const NO_VALUE = { noValue: true };

/**
 * Please note that the same topic can exist in two forms:
 * - stateful (based on BehaviorSubject) and
 * - stateless (based on Subject).
 * Please be careful when registering interests and triggering notifications.
 */
export abstract class ValuesExchangeProvider {
  public abstract registerMultipleInterests(topics: string[], subscriber: any, stateless?: boolean): Subscription;
    /**
   * Optional 'stateless' parameter signals that values exchange should create a Subject instead of
   * BehaviorSubject.
   */
  public abstract registerInterest(topic: string, subscriber: any, stateless?: boolean): Unsubscribable;

  public abstract registerOutput(topic: string, producer?: Subject<any>, stateless?: boolean): Subject<any>;
  public abstract unregisterOutput(topic: string, producer?: Subject<any>, stateless?: boolean);

  public abstract registerWithWidgetConfig(widgetConfig: any, callback: Function, stateless?: boolean): Subscription;

  /**
   * Is expected to be used by user-initiated activities (eg, activating context menu item)
   * Locates group of listeners, registered on certain topic, and notifies them with given value.
   * If topic has already existing producer, just calls next() on producer.
   * If topic has no producer yet, Subject producer is registered and value emitted.
   *
   * Please note: a combination of topic+stateless is used to determine which listeners shall be notified.
   */
  public abstract notifyListeners(topic: string, value: any, stateless?: boolean);

   /**
   * Should return last known value for that topic. Applicable only to stateful topics.
   */
  public abstract getLastKnownValue(topic: string):any;
}
